import { Button, Popover, Select, Slider, Switch, Tooltip } from "antd";
import OBCSpinner from "components/util/OBC";
import _ from "lodash";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    clearAssetData,
    clearRequestedIssue,
    customAudit,
    generateIssueAssetReport,
    getAssetData,
    getAssetGroups,
    getAssetList,
    refreshAssetObservations,
    selectAsset,
    selectObservation,
    setAssetDateFilter,
    setAssetGroupFilter,
    setAssetSearchQueryFilter,
    setGroundTruthFilter,
} from "redux/actions";
// import IssueItem from "./IssueItem";
import { CalendarOutlined, MinusOutlined, PlusOutlined, SyncOutlined } from "@ant-design/icons";
import { faInfoCircle, faSortAmountDown } from "@fortawesome/free-solid-svg-icons";
import { faFileDownload } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonGroup } from "@material-ui/core";
import Tippy from "@tippyjs/react";
import AssetItem from "components/Issues/AssetItem";
import ObservationModal from "components/Issues/ObservationModal";
import OBCToggleButton from "components/OBC/OBCToggleButton";
import moment from "moment";
import DayPicker from "react-day-picker";
import AutoSizer from "react-virtualized-auto-sizer";
import { areEqual, VariableSizeList } from "react-window";
import { currentDashboardSelector } from "redux/selectors";
import OBCButton from "../OBC/OBCButton";
import OBCSearchInput from "../OBC/OBCSearchInput";

const dateFiltersSelector = (state) => state.mlAssets.filters.date;
const searchQueryFilterSelector = (state) => state.mlAssets.filters.searchQuery;
const requestedAssetSelector = (state) => state.mlAssets.requested;
const groundTruthFilterSelector = (state) => state.mlAssets.filters.groundTruth;
const selectedIssueDataSelector = (state) => state.mlAssets.selected;
const selectedAssetSelector = (state) => state.mlAssets.selected.asset;
const assetGroupsSelector = (state) => state.mlAssets.groups;
const assetGroupFilterSelector = (state) => state.mlAssets.filters.group;
const assetObservationsSelector = (state) => state.mlAssets.observations;
const superuserSelector = (state) => state.userDetails.userConfig.super_admin;
const assetIDsSelector = (state) => state.mlAssets.ids;
const assetsDownloadEnabledSelector = (state) =>
    _.get(
        _.find(state.dashboards, (dashboard) => dashboard.access_token === state.access_token),
        ["config", "assets_download_enabled"],
        false,
    );

const ASSET_EXPORT_BLACKLIST = [
    5, // adjustment switches
    4, // joints
];

const GUTTER_SIZE = 30;
const ISSUE_ITEM_HEIGHT_CLOSED = 230;
const BOTTOM_OUT_TEMPERATURE_DEFAULT = { min: 0, max: 50 };
const MAX_EXPORT_COUNT = 5000;

const GENERIC_SORT_DATA = [
    {
        value: "last_observed",
        label: "Last Observed",
        short_title: "Most Recent",
        description: "newness observations first",
    },
    {
        value: "observations_count",
        label: "Most Observations",
        short_title: "Observations",
        description: "highest number of observations",
    },
];

const CLASS_SPECIFIC_SORT_DATA = {
    Signal: [
        {
            value: "consensus_coherence",
            label: "Consensus Coherence Desc *",
            short_title: "Coherence Desc *",
            description: "highest consensus coherence first",
            superUserOnly: true,
        },
        {
            value: "consensus_coherence_asc",
            label: "Consensus Coherence Asc *",
            short_title: "Coherence Asc *",
            description: "lowest consensus coherence first",
            superUserOnly: true,
        },
    ],
    "Structure ID Plate": [
        {
            value: "consensus_coherence",
            label: "Consensus Coherence Desc",
            short_title: "Coherence Desc",
            description: "highest consensus coherence first",
        },
        {
            value: "consensus_coherence_asc",
            label: "Consensus Coherence Asc",
            short_title: "Coherence Asc",
            description: "lowest consensus coherence first",
        },
        {
            value: "position_difference_desc",
            label: "Position Difference Desc",
            short_title: "Position Desc",
            description: "lowest position difference first",
        },
        {
            value: "position_difference_asc",
            label: "Position Difference Asc",
            short_title: "Position Asc",
            description: "largest position difference first",
        },
    ],
    Tensioner: [
        {
            value: "severity",
            label: "Green Zone Overflow (cm)",
            short_title: "Overflow",
            description: "green zone overflow",
        },
        {
            value: "bottom_out_temperature",
            label: "Bottom Out Temperature (degrees)",
            short_title: "Bottom Out Temp",
            description: "bottom out temperature ascending",
            default: true,
        },
    ],
    "Adjustment Switch Base Plate": [
        {
            value: "tip_support_asc",
            label: "Tip Support Asc",
            short_title: "Tip Support",
            description: "tip support ascending",
        },
        {
            value: "gap_desc",
            label: "Gap Desc",
            short_title: "Gap",
            description: "gap descending",
        },
        {
            value: "gap_asc",
            label: "Gap Asc",
            short_title: "Gap",
            description: "gap ascending",
        },
        {
            value: "heel_support_asc",
            label: "Heel Support Asc",
            short_title: "Heel Support",
            description: "heel support ascending",
        },
    ],
};

const DEFAULT_ADJUSTMENT_SWITCH_FILTER = {
    tip_offset: [0, 250],
    gap: [0, 250],
    heel_offset: [-40, 175],
};

const AssetsList = React.memo(({ unreadableUpdated, displayUnreadable, selectedAssetGroup }) => {
    const dispatch = useDispatch();

    const dateFilter = useSelector(dateFiltersSelector);
    const assetGroupFilter = useSelector(assetGroupFilterSelector);
    const requestedAsset = useSelector(requestedAssetSelector);
    const searchQueryFilter = useSelector(searchQueryFilterSelector);
    const groundTruthFilter = useSelector(groundTruthFilterSelector);
    const selectedAsset = useSelector(selectedAssetSelector);
    const assetGroups = useSelector(assetGroupsSelector);
    const superuser = useSelector(superuserSelector);
    const assetIDs = useSelector(assetIDsSelector);
    const downloadsEnabled = useSelector(assetsDownloadEnabledSelector);
    const currentDashboard = useSelector(currentDashboardSelector);

    const [listRef, setListRef] = useState();
    const [showFailedMatches, setShowFailedMatches] = useState(false);
    const [classification, setClassification] = useState(null);
    const [confidence, setConfidence] = useState(null);
    const [sortValue, setSortValue] = useState("last_observed");
    const [initialised, setInitialised] = useState(false);
    const [loading, setLoading] = useState(true);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [classesInitialised, setClassesInitialised] = useState(false);
    const [queryToRequest, setQueryToRequest] = useState("");
    const [showUnreadable, setShowUnreadable] = useState(false);
    const [trustLevelFilter, setTrustLevelFilter] = useState(0);
    const [displayAdditionalFilters, setDisplayAdditionalFilters] = useState(false);
    const [currentlyVisibleTooltip, setCurrentlyVisibleTooltip] = useState(null);
    const [bottomOutTemp, setBottomOutTemp] = useState({});
    const [vegetationFilter, setVegetationFilter] = useState("");
    const [jointFilter, setJointFilter] = useState("");
    const [adjustmentSwitchFilter, setAdjustmentSwitchFilter] = useState(DEFAULT_ADJUSTMENT_SWITCH_FILTER);
    const assetListRequestRef = useRef();
    const selectedAssetRef = useRef();
    const setQueryToRequestDebounce = useRef(_.debounce(setQueryToRequest, 1500));
    const dateFrom = new Date(dateFilter.from * 1000);
    const dateTo = new Date(dateFilter.to * 1000);
    const modifiers = { start: dateFrom, end: dateTo };
    const shouldShowVeg = _.get(currentDashboard, ["config", "show_ohl_veg"], true);

    // const listRef = useRef();
    const sizeMap = useRef({});
    const apiAbortController = useRef();

    const setSize = useCallback(
        (index, size) => {
            sizeMap.current = { ...sizeMap.current, [index]: size };
            if (listRef) {
                listRef.resetAfterIndex(index);
            }
        },
        [listRef],
    );

    const abortRequest = () => {
        if (apiAbortController.current) {
            apiAbortController.current.abort("user_abort");
            apiAbortController.current = null;
        }
    };

    const assetListRequest = useCallback(
        (bottomOutTemperature = {}, adjustmentSwitchFilter) => {
            if (assetGroupFilter) {
                abortRequest();
                setLoading(true);
                const abortController = new AbortController();
                apiAbortController.current = abortController;

                dispatch(clearAssetData());

                dispatch(
                    getAssetList(
                        assetGroupFilter,
                        groundTruthFilter,
                        queryToRequest,
                        sortValue,
                        showUnreadable,
                        showFailedMatches,
                        trustLevelFilter,
                        dateFilter.from,
                        dateFilter.to,
                        abortController.signal,
                        bottomOutTemperature.max >= 50 ? { ...bottomOutTemperature, max: 99999999 } : bottomOutTemperature,
                        vegetationFilter,
                        jointFilter,
                        adjustmentSwitchFilter,
                        classification,
                        confidence,
                        () => {
                            apiAbortController.current = null;
                            setInitialised(true);
                            setLoading(false);
                            if (selectedAssetRef.current) {
                                dispatch(refreshAssetObservations(selectedAssetRef.current, showFailedMatches));
                            }
                        },
                    ),
                );
            }
        },
        [
            assetGroupFilter,
            dispatch,
            groundTruthFilter,
            queryToRequest,
            sortValue,
            showUnreadable,
            showFailedMatches,
            trustLevelFilter,
            dateFilter,
            vegetationFilter,
            jointFilter,
            classification,
            confidence,
        ],
    );

    useEffect(() => {
        assetListRequestRef.current = assetListRequest;
    }, [assetListRequest]);

    const assetListRequestDebounce = useMemo(() => {
        return _.debounce((...args) => {
            assetListRequestRef.current(...args);
        }, 500);
    }, []);

    useEffect(() => {
        if (searchQueryFilter !== "") {
            setQueryToRequest(searchQueryFilter);
        }

        return () => assetListRequestDebounce.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        selectedAssetRef.current = selectedAsset;
    }, [selectedAsset]);

    useEffect(() => {
        if (assetGroupFilter === 3) {
            if (_.isEmpty(bottomOutTemp)) {
                setBottomOutTemp(BOTTOM_OUT_TEMPERATURE_DEFAULT);
            }
        } else {
            setBottomOutTemp({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetIDs]);

    useEffect(() => {
        if (
            (BOTTOM_OUT_TEMPERATURE_DEFAULT.min !== bottomOutTemp.min || BOTTOM_OUT_TEMPERATURE_DEFAULT.max !== bottomOutTemp.max) &&
            _.has(bottomOutTemp, "max") &&
            _.has(bottomOutTemp, "min")
        ) {
            assetListRequestDebounce(bottomOutTemp, {});
        }
    }, [assetListRequestDebounce, bottomOutTemp]);

    useEffect(() => {
        assetListRequestDebounce({}, adjustmentSwitchFilter);
    }, [adjustmentSwitchFilter, assetListRequestDebounce]);

    useEffect(() => {
        assetListRequest(assetGroupFilter !== 3 ? {} : bottomOutTemp, adjustmentSwitchFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetListRequest, assetGroupFilter]);

    useEffect(() => {
        if (initialised) {
            if (requestedAsset.asset) {
                dispatch(selectAsset(parseInt(requestedAsset.asset)));
                if (requestedAsset.observation) {
                    dispatch(selectObservation(parseInt(requestedAsset.observation)));
                }
                dispatch(clearRequestedIssue());
            }
        }
    }, [dispatch, initialised, requestedAsset.asset, requestedAsset.observation]);

    const onSearchQueryChanged = useCallback(
        (value) => {
            dispatch(setAssetSearchQueryFilter(value));
            setQueryToRequestDebounce.current(value);
        },
        [dispatch],
    );

    useEffect(() => {
        if (requestedAsset.asset) {
            dispatch(getAssetData(requestedAsset.asset)).then((assetData) => {
                dispatch(setAssetGroupFilter(assetData.issue_group_id));
                onSearchQueryChanged(requestedAsset.asset);
            });
        }
    }, [dispatch, requestedAsset.asset, onSearchQueryChanged]);

    useEffect(() => {
        dispatch(getAssetGroups());
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(assetGroups) && !assetGroupFilter && !classesInitialised) {
            const firstAssetGroup = Object.values(assetGroups)[0];
            dispatch(setAssetGroupFilter(firstAssetGroup.id));
            setClassesInitialised(true);
        }
    }, [assetGroupFilter, assetGroups, classesInitialised, dispatch]);

    const handleDayClick = (day) => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (day > today) {
            return;
        }
        let currentRange;

        currentRange = {
            from: new Date(dateFilter.from * 1000),
            to: new Date(dateFilter.to * 1000),
        };
        const range = DayPicker.DateUtils.addDayToRange(day, currentRange);
        if (!dateFilter.to) {
            range.to = day;
            range.from = day;
        }

        const newDateFilter = {
            to: moment(range.to).endOf("day").unix(),
            from: moment(range.from).startOf("day").unix(),
        };

        dispatch(setAssetDateFilter(newDateFilter));
    };

    const renderDatePicker = () => {
        const today = new Date();
        return (
            <>
                <DayPicker
                    className="Selectable"
                    selected={[dateFrom, { from: dateFrom, to: dateTo }]}
                    modifiers={modifiers}
                    onDayClick={(day) => handleDayClick(day)}
                    disabled={{ after: today }}
                />
                <div className="DatePickerButtons">
                    <OBCButton
                        className="ButtonReset"
                        disabled={!dateFilter.from}
                        onClick={() => dispatch(setAssetDateFilter({ from: null, to: null }))}>
                        Reset
                    </OBCButton>
                    <OBCButton
                        className="ButtonOk"
                        variant="secondary"
                        onClick={() => setDatePickerOpen(false)}>
                        Close
                    </OBCButton>
                </div>
            </>
        );
    };

    const getItemSize = (index) => {
        if (sizeMap.current[index]) {
            return Math.max(sizeMap.current[index] + GUTTER_SIZE, ISSUE_ITEM_HEIGHT_CLOSED);
        }
        return ISSUE_ITEM_HEIGHT_CLOSED; // - 30;
    };

    useEffect(() => {
        if (selectedAsset && listRef) {
            const issueIndex = Math.max(
                _.findIndex(assetIDs, (asset) => asset[0] === selectedAsset),
                0,
            );
            if (listRef) {
                listRef.resetAfterIndex(issueIndex);
                listRef.scrollToItem(issueIndex, "smart");
            }
        }

        return () => {
            if (selectedAsset) {
                const issueIndex = Math.max(
                    _.findIndex(assetIDs, (asset) => asset[1] === selectedAsset),
                    0,
                );
                if (listRef) {
                    listRef.resetAfterIndex(issueIndex);
                }
            }
        };
    }, [selectedAsset, assetIDs, listRef]);

    const Row = memo(({ index, style }) => {
        const assetID = assetIDs[index][0] || null;

        return (
            <div style={style}>
                <AssetItem
                    index={index}
                    setSize={setSize}
                    key={`${assetID}`}
                    assetID={assetID}
                    selected={parseInt(assetID) === selectedAsset}
                    searchQuery={searchQueryFilter}
                    isIssue={false}
                    minHeight={ISSUE_ITEM_HEIGHT_CLOSED}
                    showFailedMatches={showFailedMatches}
                    autoAck={false}
                />
            </div>
        );
    }, areEqual);

    const content = () => {
        if (initialised && !loading && queryToRequest === searchQueryFilter) {
            if (!assetIDs.length) {
                return (
                    <div className="NoIssuesContaner">
                        <h1 className="NoIssuesText">No Assets matching current filters</h1>
                    </div>
                );
            } else {
                return (
                    <div
                        className="IssuesList"
                        id="ScrollList">
                        <AutoSizer className="issuesAutosizer">
                            {({ height, width }) => (
                                <VariableSizeList
                                    height={height}
                                    width={width - 1}
                                    style={{ overflowX: "hidden" }}
                                    itemSize={getItemSize}
                                    ref={(ref) => setListRef(ref)}
                                    itemCount={assetIDs.length}>
                                    {Row}
                                </VariableSizeList>
                            )}
                        </AutoSizer>
                    </div>
                );
            }
        } else {
            return (
                <div className="SessionSpinner">
                    <OBCSpinner
                        size={70}
                        speed={3}
                        color={"#e8dfff"}
                    />
                </div>
            );
        }
    };

    const onClassFilterChange = (val) => {
        const selected_asset_type = _.get(assetGroups, val, {});

        if (selected_asset_type.class !== "Structure ID Plate") {
            setVegetationFilter("");
            setJointFilter("");
        }

        dispatch(
            customAudit(
                "asset_type_click",
                { class: selected_asset_type.class, display_name: selected_asset_type.display_name },
                `User clicked ${selected_asset_type.class}`,
            ),
        );

        setAdjustmentSwitchFilter(DEFAULT_ADJUSTMENT_SWITCH_FILTER);

        if (listRef) {
            listRef.resetAfterIndex(0);
        }
        dispatch(setAssetGroupFilter(val));
    };

    const sortByOptions = useMemo(() => {
        let options = [];
        let allSortData = _.concat(GENERIC_SORT_DATA, _.get(CLASS_SPECIFIC_SORT_DATA, selectedAssetGroup.class, {}));

        options = allSortData
            .filter((option) => !option.superUserOnly || (option.superUserOnly && superuser))
            .map((option) => (
                <Select.Option
                    key={option.value}
                    value={option.value}
                    label={
                        <>
                            <FontAwesomeIcon
                                icon={faSortAmountDown}
                                style={{ marginRight: "5px" }}
                            />
                            {option.short_title}
                        </>
                    }>
                    {option.label}
                </Select.Option>
            ));

        return options;
    }, [selectedAssetGroup.class, superuser]);

    const toggleAdditionalFilters = () => {
        if (displayAdditionalFilters) {
            setTrustLevelFilter(0);
            setShowUnreadable(false);
            setShowFailedMatches(false);
            setVegetationFilter("");
            setJointFilter("");
            setAdjustmentSwitchFilter(DEFAULT_ADJUSTMENT_SWITCH_FILTER);
        }
        setDisplayAdditionalFilters(!displayAdditionalFilters);
    };

    const handleReportRequest = useCallback(
        (exportWithImages) => {
            const filteredIds = assetIDs.map((row) => row[0]);

            const parameters = {
                asset_group_id: assetGroupFilter,
                disable_images: !exportWithImages,
                observation_group_ids: filteredIds,
            };

            dispatch(generateIssueAssetReport(parameters, "asset_report_export"));
        },
        [assetGroupFilter, assetIDs, dispatch],
    );

    const adjustmentSwitchFilters = useMemo(() => {
        if (assetGroupFilter === 5) {
            return (
                <div className="Row AdjustmentSwitchFilters">
                    {_.map(
                        [
                            { filterType: "tip_offset", displayName: "Tip Support" },
                            { filterType: "gap", displayName: "Gap" },
                            { filterType: "heel_offset", displayName: "Heel Support" },
                        ],
                        (item) => {
                            return (
                                <div className="AdjustmentSwitchFilter">
                                    <p className="label">{item.displayName}: </p>
                                    <p>{DEFAULT_ADJUSTMENT_SWITCH_FILTER[item.filterType][0]}mm</p>
                                    <Slider
                                        className="AdjustmentSwitchSlider"
                                        range
                                        min={DEFAULT_ADJUSTMENT_SWITCH_FILTER[item.filterType][0]}
                                        max={DEFAULT_ADJUSTMENT_SWITCH_FILTER[item.filterType][1]}
                                        value={adjustmentSwitchFilter[item.filterType]}
                                        onChange={(e) => setAdjustmentSwitchFilter((filter) => ({ ...filter, [item.filterType]: e }))}
                                    />
                                    <p>{DEFAULT_ADJUSTMENT_SWITCH_FILTER[item.filterType][1]}mm</p>
                                </div>
                            );
                        },
                    )}
                </div>
            );
        }

        return null;
    }, [assetGroupFilter, adjustmentSwitchFilter]);

    const noAssets = useMemo(() => assetIDs.length < 1, [assetIDs.length]);
    const tooManyAssets = useMemo(() => assetIDs.length > MAX_EXPORT_COUNT, [assetIDs.length]);
    const exportDisabled = useMemo(() => noAssets || tooManyAssets || loading, [loading, noAssets, tooManyAssets]);
    const tooltipTitleText = useMemo(() => {
        if (loading) return "Loading";

        if (tooManyAssets) return `The number of filtered assets exceeds the maximum limit of ${MAX_EXPORT_COUNT.toLocaleString()}`;

        if (noAssets) return "No assets to export";

        return null;
    }, [loading, noAssets, tooManyAssets]);

    return (
        <div className="IssuesContentTab Assets">
            <div className="IssueFilterContainer">
                <div className="LeftColumn">
                    <div className="Row">
                        <div className="IssuesFilterSelect">
                            <div
                                className="FilterButton"
                                onClick={() => assetListRequest(bottomOutTemp)}>
                                <SyncOutlined className="icon" />
                            </div>
                        </div>

                        <div className="FilterItemDatePicker">
                            <Tooltip
                                overlayClassName="IssueTabDatePicker"
                                overlayStyle={{ opacity: 1 }}
                                title={renderDatePicker}
                                visible={datePickerOpen}
                                placement="bottomLeft"
                                trigger="click"
                                onVisibleChange={(visible) => setDatePickerOpen(visible)}>
                                <OBCToggleButton
                                    toggled={datePickerOpen}
                                    active={dateFilter.from}>
                                    <CalendarOutlined />
                                </OBCToggleButton>
                            </Tooltip>
                        </div>

                        <div className="IssuesFilterSelect">
                            <span>Type</span>
                            <Select
                                value={assetGroupFilter}
                                onChange={(val) => onClassFilterChange(val)}
                                dropdownMatchSelectWidth={false}>
                                {Object.values(assetGroups).map((issueGroup) => {
                                    let nameToDisplay = `${issueGroup.class}`;
                                    if (issueGroup.display_name) {
                                        nameToDisplay = issueGroup.display_name;
                                    }
                                    return (
                                        <Select.Option
                                            value={issueGroup.id}
                                            key={issueGroup.id}>
                                            {nameToDisplay}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                            {/* <Select value={classFilter} onChange={val => onClassFilterChange(val)} dropdownMatchSelectWidth={false}>
                                {classOptions.map(issueClass => {
                                    return <Select.Option value={issueClass} key={issueClass}>
                                        {_.get(assetGroups, [issueClass, "display_name"], issueClass) || issueClass}
                                    </Select.Option>
                                })}
                            </Select> */}
                        </div>
                        {superuser && _.get(assetGroups, assetGroupFilter, {}).class === "Signal" && (
                            <>
                                <div className="IssuesFilterSelect">
                                    <span>Ladder/Screen</span>
                                    <Select
                                        value={classification}
                                        onChange={setClassification}
                                        dropdownMatchSelectWidth={false}>
                                        {[
                                            { value: null, label: "All" },
                                            { value: "ladder", label: "Includes ladder" },
                                            { value: "screen", label: "Includes screen" },
                                            { value: "unknown", label: "Presence unknown" },
                                        ].map(({ value, label }) => (
                                            <Select.Option
                                                value={value}
                                                key={value}>
                                                {label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="IssuesFilterSelect">
                                    <span>AI Confidence</span>
                                    <Select
                                        value={confidence}
                                        onChange={setConfidence}
                                        dropdownMatchSelectWidth={false}>
                                        {[
                                            { value: null, label: "All" },
                                            { value: "max", label: "Max" },
                                            { value: "high", label: "High" },
                                            { value: "low", label: "Low" },
                                            { value: "not_max", label: "Not Max" },
                                        ].map(({ value, label }) => (
                                            <Select.Option
                                                value={value}
                                                key={value}>
                                                {label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            </>
                        )}
                        <div className="IssuesFilterSelect">
                            <span>Sort</span>
                            <Select
                                optionLabelProp="label"
                                value={sortValue}
                                onChange={setSortValue}
                                dropdownMatchSelectWidth={false}>
                                {sortByOptions}
                            </Select>
                        </div>
                        {/* Hiding report download for adjustment switches as not supported */}
                        {downloadsEnabled && !_.includes(ASSET_EXPORT_BLACKLIST, assetGroupFilter) && (
                            <div className="IssuesFilterSelect">
                                <span>Export</span>
                                <Tooltip
                                    mouseEnterDelay={0.5}
                                    title={tooltipTitleText}
                                    visible={currentlyVisibleTooltip === "report" && tooltipTitleText}
                                    onVisibleChange={(val) => setCurrentlyVisibleTooltip(!val || "report")}>
                                    <div className="RequestIssuesReport">
                                        <Popover
                                            title={`${_.get(selectedAssetGroup, ["display_name"], selectedAssetGroup.class) || selectedAssetGroup.class} Report`}
                                            content={
                                                <div className="ThermalIssueReportPopover">
                                                    <ButtonGroup className="w-fit">
                                                        <Button
                                                            type="primary"
                                                            style={{ marginRight: 5 }}
                                                            onClick={() => handleReportRequest(false)}
                                                            disabled={exportDisabled}>
                                                            Export without images
                                                        </Button>
                                                        <Button
                                                            type="primary"
                                                            onClick={() => handleReportRequest(true)}
                                                            disabled={exportDisabled}>
                                                            Export with images
                                                        </Button>
                                                    </ButtonGroup>
                                                </div>
                                            }
                                            trigger="hover"
                                            placement="bottom">
                                            {/* <p>TESTING HERE</p> */}
                                            <OBCToggleButton
                                                variant="primary-antd"
                                                disabled={exportDisabled}
                                                icon={faFileDownload}
                                            />
                                        </Popover>
                                    </div>
                                </Tooltip>
                            </div>
                        )}
                        <div className="IssuesFilterSelect">
                            <Tippy
                                content={`${displayAdditionalFilters ? "Hide" : "View"} additional filters`}
                                placement="top"
                                duration="500">
                                <div
                                    className="FilterButton"
                                    onClick={toggleAdditionalFilters}>
                                    {displayAdditionalFilters ? <MinusOutlined className="icon" /> : <PlusOutlined className="icon" />}
                                </div>
                            </Tippy>
                        </div>
                    </div>

                    {displayAdditionalFilters && (
                        <>
                            <div className="Row">
                                <div className="IssuesFilterSelect">
                                    <span>Ground Truth Data</span>
                                    <Select
                                        value={groundTruthFilter}
                                        onChange={(val) => dispatch(setGroundTruthFilter(val))}
                                        dropdownMatchSelectWidth={false}>
                                        <Select.Option value={null}>All</Select.Option>
                                        <Select.Option value={true}>Set</Select.Option>
                                        <Select.Option value={false}>Not Set</Select.Option>
                                    </Select>
                                </div>
                                <div className="IssuesFilterSelect">
                                    <span>
                                        Trust Level
                                        <Tippy
                                            theme="aivr"
                                            placement="right"
                                            content={
                                                <span>
                                                    Trust level is calculated based on whether the asset has been verified <br />
                                                    by a human, detected by aivr and matches external known asset data.
                                                </span>
                                            }>
                                            <span style={{ marginLeft: 4 }}>
                                                <FontAwesomeIcon
                                                    className="InfoIcon"
                                                    icon={faInfoCircle}
                                                />
                                            </span>
                                        </Tippy>
                                    </span>
                                    <Select
                                        value={trustLevelFilter}
                                        onChange={setTrustLevelFilter}
                                        dropdownMatchSelectWidth={false}
                                        style={{ width: "90px" }}>
                                        <Select.Option value={0}>All</Select.Option>
                                        <Select.Option value={1}>Level 1</Select.Option>
                                        <Select.Option value={2}>Level 2</Select.Option>
                                        <Select.Option value={3}>Level 3</Select.Option>
                                        <Select.Option value={4}>Level 4</Select.Option>
                                        <Select.Option value={5}>Level 5</Select.Option>
                                        <Select.Option value={6}>Level 6</Select.Option>
                                    </Select>
                                </div>
                                {_.get(assetGroups, assetGroupFilter, {}).class === "Structure ID Plate" && shouldShowVeg && (
                                    <div className="IssuesFilterSelect">
                                        <span>Vegetation</span>
                                        <Select
                                            value={vegetationFilter}
                                            onChange={(e) => setVegetationFilter(e)}
                                            style={{ width: "160px" }}
                                            dropdownMatchSelectWidth={false}>
                                            <Select.Option value={""}>All</Select.Option>
                                            <Select.Option value={"any"}>Any vegetation observed</Select.Option>
                                            <Select.Option value={"ok"}>Acceptable vegetation observed</Select.Option>
                                            <Select.Option value={"vegetation_around_base"}>Vegetation around the base</Select.Option>
                                            <Select.Option value={"bad"}>Unacceptable vegetation observed</Select.Option>
                                        </Select>
                                    </div>
                                )}
                                {_.get(assetGroups, assetGroupFilter, {}).class === "Joint" && (
                                    <div className="IssuesFilterSelect">
                                        <span>Joint Type</span>
                                        <Select
                                            value={jointFilter}
                                            onChange={(e) => setJointFilter(e)}
                                            style={{ width: "160px" }}
                                            dropdownMatchSelectWidth={false}>
                                            <Select.Option value={""}>Not set</Select.Option>
                                            <Select.Option value={"Four_Bolt_Encapsulated"}>Four Bolt Encapsulated</Select.Option>
                                            <Select.Option value={"Four_Bolt_Glued"}>Four Bolt Glued</Select.Option>
                                            <Select.Option value={"Four_Bolt_Expansion"}>Four Bolt Expansion</Select.Option>
                                            <Select.Option value={"Six_Bolt_Encapsulated"}>Six Bolt Encapsulated</Select.Option>
                                            <Select.Option value={"Six_Bolt_Glued"}>Six Bolt Glued</Select.Option>
                                            <Select.Option value={"Tight"}>Tight Joint</Select.Option>
                                            <Select.Option value={"Temporary_Joint"}>Temporary Joint</Select.Option>
                                            <Select.Option value={"Other"}>Other</Select.Option>
                                        </Select>
                                    </div>
                                )}
                                {superuser && displayUnreadable && (
                                    <div className="IssuesFilterSelect">
                                        <span>Unreadable</span>
                                        <div className="SwitchContainer">
                                            <Switch
                                                className="Switch"
                                                checked={showUnreadable}
                                                onChange={(checked) => setShowUnreadable(checked)}
                                            />
                                        </div>
                                    </div>
                                )}
                                {superuser && (
                                    <div className="IssuesFilterSelect">
                                        <span>Show failed</span>
                                        <div className="SwitchContainer">
                                            <Switch
                                                className="Switch"
                                                checked={showFailedMatches}
                                                onChange={(checked) => setShowFailedMatches(checked)}
                                            />
                                        </div>
                                    </div>
                                )}
                                {assetGroupFilter === 3 && (
                                    <div className="IssuesFilterSlider">
                                        <div className="SliderLabel">
                                            <span>Bottom out temperature</span>{" "}
                                            {!_.isEmpty(bottomOutTemp) &&
                                                (bottomOutTemp.min === BOTTOM_OUT_TEMPERATURE_DEFAULT.min &&
                                                bottomOutTemp.max === BOTTOM_OUT_TEMPERATURE_DEFAULT.max ? (
                                                    <span className="MaxTemp">[showing all]</span>
                                                ) : (
                                                    <span>
                                                        [{bottomOutTemp.min} -{" "}
                                                        <span className="MaxTemp">{bottomOutTemp.max === 50 ? "50°C+" : `${bottomOutTemp.max}°C`}</span>]
                                                    </span>
                                                ))}
                                        </div>
                                        <Slider
                                            className="BottomOutTemp"
                                            range
                                            min={BOTTOM_OUT_TEMPERATURE_DEFAULT.min}
                                            max={BOTTOM_OUT_TEMPERATURE_DEFAULT.max}
                                            tooltipVisible={false}
                                            onChange={(e) => setBottomOutTemp({ min: e[0], max: e[1] })}
                                            value={[bottomOutTemp.min, bottomOutTemp.max]}
                                            marks={{
                                                [BOTTOM_OUT_TEMPERATURE_DEFAULT.min]: {
                                                    style: {
                                                        color: "#fff",
                                                    },
                                                    label: <strong>{BOTTOM_OUT_TEMPERATURE_DEFAULT.min}°C</strong>,
                                                },
                                                [BOTTOM_OUT_TEMPERATURE_DEFAULT.max]: {
                                                    style: {
                                                        color: "#ffb100",
                                                    },
                                                    label: <strong>{BOTTOM_OUT_TEMPERATURE_DEFAULT.max}°C+</strong>,
                                                },
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            {adjustmentSwitchFilters}
                        </>
                    )}
                </div>
                <div className="RightColumn">
                    <OBCSearchInput
                        id="AssetsFilterSearchInput"
                        className="IssuesFilterSearchInput"
                        placeholder="Asset ID or ELR"
                        onChange={onSearchQueryChanged}
                        value={searchQueryFilter}
                    />
                </div>
            </div>
            {!loading && <p className="SelectedAssetsCount">Selected asset count: {assetIDs.length}</p>}
            {content()}
        </div>
    );
});

const AssetsTab = () => {
    const dispatch = useDispatch();
    const selectedData = useSelector(selectedIssueDataSelector);
    const assetObservations = useSelector(assetObservationsSelector);
    const assetGroupFilter = useSelector(assetGroupFilterSelector);
    const assetGroups = useSelector(assetGroupsSelector);

    const [unreadableUpdated, setUnreadableUpdated] = useState(false);

    const selectedAssetGroup = useMemo(() => {
        return _.get(assetGroups, assetGroupFilter, {});
    }, [assetGroups, assetGroupFilter]);

    const displayUnreadable = useMemo(() => {
        return ["Signal", "Structure ID Plate"].includes(selectedAssetGroup.class);
    }, [selectedAssetGroup]);

    return (
        <>
            {!_.isNil(selectedData.observation) && (
                <ObservationModal
                    isIssue={false}
                    closeModal={() => dispatch(selectObservation(null))}
                    observations={assetObservations.observations}
                    unreadableUpdated={unreadableUpdated}
                    setUnreadableUpdated={(e) => setUnreadableUpdated(e)}
                    displayUnreadable={displayUnreadable}
                />
            )}
            <AssetsList
                unreadableUpdated={unreadableUpdated}
                displayUnreadable={displayUnreadable}
                selectedAssetGroup={selectedAssetGroup}
            />
        </>
    );
};

export default AssetsTab;
